import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Plus, ChevronRight, ChevronDown, Edit2, Trash2, Target } from 'lucide-react';
import { format } from 'date-fns';
import { api } from '../api/client';
import { Modal, ProgressBar, EmptyState, Confirm, toast, StatusBadge } from './UI';
import { useApp } from '../contexts/AppContext';
import TaskModal from './TaskModal';

const COLORS = ['#D4A853', '#52A878', '#5B8FD4', '#9B72CF', '#E0804A', '#E05050'];
const CURRENT_YEAR = new Date().getFullYear();

function quarterPeriodDate(year, quarter) {
  return format(new Date(year, (quarter - 1) * 3, 1), 'yyyy-MM-dd');
}

function getQuarterFromPeriodDate(periodDate) {
  if (!periodDate) return null;
  const m = new Date(periodDate.split('T')[0]).getMonth();
  return Math.floor(m / 3) + 1;
}
function getYearFromPeriodDate(periodDate) {
  if (!periodDate) return null;
  return new Date(periodDate.split('T')[0]).getFullYear();
}

export default function OKRView() {
  const { tasks, refreshTasks, isDone } = useApp();
  const [year, setYear] = useState(CURRENT_YEAR);
  const [goals, setGoals] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [goalModal, setGoalModal] = useState(null); // { item }
  const [taskModalConfig, setTaskModalConfig] = useState(null); // { task?, initial? }
  const [confirmDel, setConfirmDel] = useState(null);

  const loadGoals = useCallback(async () => {
    const g = await api.getGoals(year);
    setGoals(Array.isArray(g) ? g : []);
  }, [year]);

  useEffect(() => { loadGoals(); }, [loadGoals]);

  const toggle = (id) => setExpanded(e => ({ ...e, [id]: !e[id] }));

  // Index tasks by structure
  const quarterTasksByGoal = useMemo(() => {
    const m = new Map(); // goal_id → tasks[]
    const orphans = []; // quarter tasks without goal_id
    for (const t of tasks) {
      if (t.scope !== 'quarter') continue;
      if (getYearFromPeriodDate(t.period_date) !== year) continue;
      if (t.goal_id) {
        if (!m.has(t.goal_id)) m.set(t.goal_id, []);
        m.get(t.goal_id).push(t);
      } else {
        orphans.push(t);
      }
    }
    // Sort by quarter
    m.forEach(arr => arr.sort((a, b) => getQuarterFromPeriodDate(a.period_date) - getQuarterFromPeriodDate(b.period_date)));
    orphans.sort((a, b) => getQuarterFromPeriodDate(a.period_date) - getQuarterFromPeriodDate(b.period_date));
    return { byGoal: m, orphans };
  }, [tasks, year]);

  const keyResultsByParent = useMemo(() => {
    const m = new Map();
    for (const t of tasks) {
      if (t.scope !== 'key_result' || !t.parent_id) continue;
      if (!m.has(t.parent_id)) m.set(t.parent_id, []);
      m.get(t.parent_id).push(t);
    }
    m.forEach(arr => arr.sort((a, b) => new Date(a.created_at) - new Date(b.created_at)));
    return m;
  }, [tasks]);

  const childrenByParent = useMemo(() => {
    const m = new Map();
    for (const t of tasks) {
      if (!t.parent_id) continue;
      if (!m.has(t.parent_id)) m.set(t.parent_id, []);
      m.get(t.parent_id).push(t);
    }
    return m;
  }, [tasks]);

  const handleDeleteGoal = async () => {
    try {
      await api.deleteGoal(confirmDel.id);
      toast('Gelöscht', 'success');
      setConfirmDel(null);
      loadGoals();
      refreshTasks();
    } catch (err) { toast(err.message, 'error'); }
  };

  const openTask = (t) => setTaskModalConfig({ task: t });
  const newQuarterTask = (goalId, defaultQuarter = 1) =>
    setTaskModalConfig({ initial: { scope: 'quarter', goal_id: goalId, period_date: quarterPeriodDate(year, defaultQuarter) } });
  const newKR = (quarterTaskId) =>
    setTaskModalConfig({ initial: { scope: 'key_result', parent_id: quarterTaskId } });
  const newInitiative = (krTaskId) =>
    setTaskModalConfig({ initial: { scope: 'task', parent_id: krTaskId } });

  return (
    <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <h1 style={{ fontFamily: 'var(--font-serif)', fontWeight: 400, fontSize: 28, flex: 1 }}>OKRs</h1>
        <select value={year} onChange={e => setYear(+e.target.value)} style={{ width: 'auto' }}>
          {[CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1].map(y => <option key={y}>{y}</option>)}
        </select>
        <button className="btn btn-primary" onClick={() => setGoalModal({ item: null })}>
          <Plus size={14} /> Jahres-Goal
        </button>
      </div>

      {goals.length === 0 && quarterTasksByGoal.orphans.length === 0 && (
        <EmptyState icon="🎯" title="Noch keine Goals"
          subtitle="Definiere übergeordnete Jahresziele, dann ordne Quartalsziele zu"
          action={<button className="btn btn-primary btn-sm" onClick={() => setGoalModal({ item: null })}><Plus size={13} /> Erstes Goal</button>} />
      )}

      {/* Goals */}
      {goals.map(goal => (
        <GoalBlock key={goal.id}
          goal={goal}
          quarterTasks={quarterTasksByGoal.byGoal.get(goal.id) || []}
          keyResultsByParent={keyResultsByParent}
          childrenByParent={childrenByParent}
          expanded={expanded} toggle={toggle}
          onEditGoal={() => setGoalModal({ item: goal })}
          onDeleteGoal={() => setConfirmDel({ type: 'goal', id: goal.id })}
          onAddQuarterTask={(q) => newQuarterTask(goal.id, q)}
          onAddKR={newKR}
          onAddInitiative={newInitiative}
          openTask={openTask}
        />
      ))}

      {/* Orphan quarter tasks (without goal) */}
      {quarterTasksByGoal.orphans.length > 0 && (
        <div>
          <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>
            Quartalsziele ohne Jahres-Goal
          </div>
          {quarterTasksByGoal.orphans.map(qt => (
            <QuarterBlock key={qt.id}
              qt={qt}
              keyResults={keyResultsByParent.get(qt.id) || []}
              childrenByParent={childrenByParent}
              expanded={expanded} toggle={toggle}
              openTask={openTask}
              onAddKR={() => newKR(qt.id)}
              onAddInitiative={newInitiative}
            />
          ))}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <button className="btn btn-ghost btn-sm" onClick={() => newQuarterTask(null)}>
          <Plus size={12} /> Quartalsziel ohne Jahres-Goal
        </button>
      </div>

      {goalModal && (
        <GoalEditModal
          goal={goalModal.item}
          year={year}
          onClose={() => setGoalModal(null)}
          onSaved={() => { setGoalModal(null); loadGoals(); }}
        />
      )}

      {taskModalConfig && (
        <TaskModal
          task={taskModalConfig.task || null}
          initial={taskModalConfig.initial || null}
          onClose={() => setTaskModalConfig(null)}
          onSaved={refreshTasks}
          onNavigate={(t) => setTaskModalConfig({ task: t })}
        />
      )}

      {confirmDel && (
        <Confirm
          message="Jahres-Goal wirklich löschen? Verknüpfte Quartalsziele bleiben bestehen, verlieren aber den Goal-Bezug."
          onConfirm={handleDeleteGoal}
          onCancel={() => setConfirmDel(null)}
        />
      )}
    </div>
  );
}

function GoalBlock({ goal, quarterTasks, keyResultsByParent, childrenByParent, expanded, toggle, onEditGoal, onDeleteGoal, onAddQuarterTask, onAddKR, onAddInitiative, openTask }) {
  const isExpanded = expanded[goal.id];
  const avgProgress = quarterTasks.length === 0 ? 0
    : Math.round(quarterTasks.reduce((sum, t) => sum + (t.progress || 0), 0) / quarterTasks.length);

  return (
    <div style={{ border: `1px solid var(--border)`, borderLeft: `4px solid ${goal.color}`, borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
      <div style={{ background: 'var(--bg-1)', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}
        onClick={() => toggle(goal.id)}>
        {isExpanded ? <ChevronDown size={16} color="var(--text-3)" /> : <ChevronRight size={16} color="var(--text-3)" />}
        <Target size={16} style={{ color: goal.color }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 15 }}>{goal.title}</div>
          {goal.description && <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{goal.description}</div>}
        </div>
        <div style={{ minWidth: 160, marginRight: 8 }}>
          <ProgressBar value={avgProgress} color={goal.color} />
          <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2, textAlign: 'right' }}>
            {quarterTasks.length} Q-Ziele · ⌀ {avgProgress}%
          </div>
        </div>
        <button className="btn btn-icon btn-ghost btn-sm" onClick={e => { e.stopPropagation(); onEditGoal(); }}><Edit2 size={12} /></button>
        <button className="btn btn-icon btn-danger btn-sm" onClick={e => { e.stopPropagation(); onDeleteGoal(); }}><Trash2 size={12} /></button>
      </div>

      {isExpanded && (
        <div style={{ padding: '8px 16px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {quarterTasks.length === 0 && <div style={{ color: 'var(--text-3)', fontSize: 13, padding: '8px 0' }}>Noch keine Quartalsziele</div>}
          {quarterTasks.map(qt => (
            <QuarterBlock key={qt.id}
              qt={qt}
              keyResults={keyResultsByParent.get(qt.id) || []}
              childrenByParent={childrenByParent}
              expanded={expanded} toggle={toggle}
              openTask={openTask}
              onAddKR={() => onAddKR(qt.id)}
              onAddInitiative={onAddInitiative}
            />
          ))}
          <div style={{ display: 'flex', gap: 8, paddingTop: 4 }}>
            {[1, 2, 3, 4].map(q => (
              <button key={q} className="btn btn-ghost btn-sm" onClick={() => onAddQuarterTask(q)}>
                <Plus size={11} /> Q{q}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function QuarterBlock({ qt, keyResults, childrenByParent, expanded, toggle, openTask, onAddKR, onAddInitiative }) {
  const isExpanded = expanded[qt.id];
  const quarter = getQuarterFromPeriodDate(qt.period_date);
  return (
    <div style={{ background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
      <div style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
        onClick={() => toggle(qt.id)}>
        {isExpanded ? <ChevronDown size={13} color="var(--text-3)" /> : <ChevronRight size={13} color="var(--text-3)" />}
        <span style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 500, minWidth: 28 }}>Q{quarter}</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 500, fontSize: 13, cursor: 'pointer' }}
            onClick={(e) => { e.stopPropagation(); openTask(qt); }}>
            {qt.title}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
            <ProgressBar value={qt.progress || 0} color="var(--accent)" />
            <span style={{ fontSize: 11, color: 'var(--text-3)', whiteSpace: 'nowrap' }}>{qt.progress || 0}%</span>
          </div>
        </div>
        <StatusBadge status={qt.status} />
        <button className="btn btn-icon btn-ghost btn-sm" onClick={e => { e.stopPropagation(); openTask(qt); }} title="Öffnen"><Edit2 size={11} /></button>
      </div>

      {isExpanded && (
        <div style={{ padding: '4px 14px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {keyResults.length === 0 && <div style={{ color: 'var(--text-3)', fontSize: 12 }}>Keine Key Results</div>}
          {keyResults.map(kr => (
            <KRBlock key={kr.id}
              kr={kr}
              initiatives={(childrenByParent.get(kr.id) || []).filter(c => c.scope !== 'key_result')}
              expanded={expanded} toggle={toggle}
              openTask={openTask}
              onAddInitiative={() => onAddInitiative(kr.id)}
            />
          ))}
          <button className="btn btn-ghost btn-sm" onClick={onAddKR} style={{ alignSelf: 'flex-start' }}>
            <Plus size={11} /> Key Result
          </button>
        </div>
      )}
    </div>
  );
}

function KRBlock({ kr, initiatives, expanded, toggle, openTask, onAddInitiative }) {
  const isExpanded = expanded[kr.id];
  return (
    <div style={{ background: 'var(--bg-3)', borderRadius: 'var(--radius)', padding: '8px 12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
        onClick={() => toggle(kr.id)}>
        {isExpanded ? <ChevronDown size={11} color="var(--text-3)" /> : <ChevronRight size={11} color="var(--text-3)" />}
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, fontWeight: 500, cursor: 'pointer' }}
            onClick={(e) => { e.stopPropagation(); openTask(kr); }}>
            {kr.title}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
            <ProgressBar value={kr.progress || 0} color="var(--green)" />
            <span style={{ fontSize: 11, color: 'var(--text-3)', whiteSpace: 'nowrap' }}>{kr.progress || 0}%</span>
          </div>
        </div>
        <StatusBadge status={kr.status} />
        <button className="btn btn-icon btn-ghost btn-sm" onClick={e => { e.stopPropagation(); openTask(kr); }}><Edit2 size={11} /></button>
      </div>

      {isExpanded && (
        <div style={{ marginTop: 8, paddingLeft: 16, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {initiatives.length === 0 && (
            <div style={{ color: 'var(--text-3)', fontSize: 11 }}>Keine Initiativen / Maßnahmen</div>
          )}
          {initiatives.map(it => (
            <div key={it.id}
              onClick={() => openTask(it)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '4px 8px', fontSize: 12, cursor: 'pointer',
                background: 'var(--bg-2)', borderRadius: 4,
              }}>
              <span style={{ flex: 1, color: isDone(it) ? 'var(--text-3)' : 'var(--text)', textDecoration: isDone(it) ? 'line-through' : 'none' }}>
                {it.title}
              </span>
              <StatusBadge status={it.status} />
            </div>
          ))}
          <button className="btn btn-ghost btn-sm" onClick={onAddInitiative} style={{ alignSelf: 'flex-start' }}>
            <Plus size={10} /> Initiative
          </button>
        </div>
      )}
    </div>
  );
}

function GoalEditModal({ goal, year, onClose, onSaved }) {
  const [form, setForm] = useState(goal || { title: '', description: '', color: COLORS[0] });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      if (goal) await api.updateGoal(goal.id, { ...form, year });
      else await api.createGoal({ ...form, year });
      toast('Gespeichert', 'success');
      onSaved();
    } catch (err) { toast(err.message, 'error'); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={goal ? 'Jahres-Goal bearbeiten' : 'Neues Jahres-Goal'} onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div className="form-group">
          <label>Titel *</label>
          <input value={form.title || ''} onChange={e => set('title', e.target.value)} autoFocus />
        </div>
        <div className="form-group">
          <label>Beschreibung</label>
          <textarea value={form.description || ''} onChange={e => set('description', e.target.value)} rows={2} />
        </div>
        <div className="form-group">
          <label>Farbe</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {COLORS.map(c => (
              <div key={c} onClick={() => set('color', c)} style={{
                width: 28, height: 28, borderRadius: '50%', background: c, cursor: 'pointer',
                border: form.color === c ? '2px solid var(--text)' : '2px solid transparent',
              }} />
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost" onClick={onClose}>Abbrechen</button>
          <button className="btn btn-primary" onClick={save} disabled={saving || !form.title?.trim()}>
            {saving ? 'Speichern...' : 'Speichern'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
